/**
 * The four (model, ability) flag keys, matching the backend's
 * UserModelPermission::ABILITY_COLUMNS. Kept in its own module so SFCs using
 * `<script setup>` can consume the type without going through `defineExpose`.
 */
export type AbilityFlagKey =
  | 'can_view'
  | 'can_create'
  | 'can_update'
  | 'can_delete';

/**
 * Ordered list of cumulative permission levels surfaced in the
 * AbilityMultiSelect dropdown. Picking a level grants every lower
 * level — "create" grants view + edit + create. Edit sits below Create:
 * being able to add records implies being able to modify them, but plain
 * edit access does not imply create. The ordering is load-bearing:
 * `flagsForLevel()` walks it to build the flag map.
 */
export type AbilityLevel = 'none' | 'view' | 'edit' | 'create' | 'delete';

/**
 * Per-level flag deltas. Each level stacks one bit on top of the lower
 * levels; "view" is just `can_view` (the backend dropped `can_view_any`).
 */
const LEVEL_FLAG_SEQUENCE: ReadonlyArray<{ level: AbilityLevel; flags: AbilityFlagKey[] }> = [
  { level: 'none', flags: [] },
  { level: 'view', flags: ['can_view'] },
  { level: 'edit', flags: ['can_update'] },
  { level: 'create', flags: ['can_create'] },
  { level: 'delete', flags: ['can_delete'] },
] as const;

/**
 * Translate a level into the cumulative flag map it implies. Returns a
 * fresh object on each call — callers can mutate freely without
 * touching the source-of-truth constant.
 */
export function flagsForLevel(level: AbilityLevel): Record<AbilityFlagKey, boolean> {
  const grant: Record<AbilityFlagKey, boolean> = {
    can_view: false,
    can_create: false,
    can_update: false,
    can_delete: false,
  };
  for (const entry of LEVEL_FLAG_SEQUENCE) {
    for (const flag of entry.flags) {
      grant[flag] = true;
    }
    if (entry.level === level) {
      return grant;
    }
  }
  return grant;
}

/**
 * Derive the highest-set level from a flag map. Non-cumulative inputs
 * (e.g. can_delete=true without the lower bits) round up to the highest
 * true bit; picking from the dropdown rewrites them cleanly.
 */
export function levelFromFlags(flags: Partial<Record<AbilityFlagKey, boolean>>): AbilityLevel {
  if (flags.can_delete) return 'delete';
  if (flags.can_create) return 'create';
  if (flags.can_update) return 'edit';
  if (flags.can_view) return 'view';
  return 'none';
}

/**
 * The bit each cumulative level turns ON, in level order. `none` owns no bit.
 */
const GRANTABLE_LEVEL_BITS: ReadonlyArray<{ level: AbilityLevel; bit: AbilityFlagKey | null }> = [
  { level: 'none', bit: null },
  { level: 'view', bit: 'can_view' },
  { level: 'edit', bit: 'can_update' },
  { level: 'create', bit: 'can_create' },
  { level: 'delete', bit: 'can_delete' },
];

/**
 * The highest level a grantor holding `heldFlags` may grant for a model.
 * Levels are cumulative, so the grantable set is a prefix: granting `create`
 * also turns on view + create, so the grantor must hold every bit up to and
 * including the level. The first missing bit caps the grantable level.
 *
 * Mirrors the backend privilege-bounding guard in PermissionController::update
 * (a non-developer can't grant a (model, ability) bit they don't hold).
 */
export function maxGrantableLevel(
  heldFlags: Partial<Record<AbilityFlagKey, boolean>>,
): AbilityLevel {
  let maxLevel: AbilityLevel = 'none';
  for (const entry of GRANTABLE_LEVEL_BITS) {
    if (entry.bit !== null && !(heldFlags[entry.bit] ?? false)) {
      break;
    }
    maxLevel = entry.level;
  }
  return maxLevel;
}

/**
 * Numeric rank of a level for ordering comparisons. none < view < edit <
 * create < delete. Exported so both the matrix and the picker bound against
 * the same scale.
 */
export const LEVEL_RANK: Readonly<Record<AbilityLevel, number>> = {
  none: 0,
  view: 1,
  edit: 2,
  create: 3,
  delete: 4,
};

/**
 * The four flag keys in declaration order. Convenient for any helper that
 * needs to iterate them without re-typing the literal list.
 */
export const ABILITY_FLAG_KEYS: ReadonlyArray<AbilityFlagKey> = [
  'can_view',
  'can_create',
  'can_update',
  'can_delete',
];

/**
 * "View" cumulative bit map — used as the seed-default for a freshly
 * declared row when no baseline is supplied. Same shape as
 * `flagsForLevel('view')`; named for the use site rather than the level
 * so callers grep for "default" not "view".
 */
export const DEFAULT_ABILITY_FLAGS: Readonly<Record<AbilityFlagKey, boolean>> =
  Object.freeze(flagsForLevel('view'));

/**
 * Copy all five flag values from `source` into `target` in place. Used by
 * the cross-view sync (multiple peer rows need the same bits set) so the
 * field-by-field copy is consolidated to one helper.
 */
export function copyFlags(
  target: Record<AbilityFlagKey, boolean>,
  source: Readonly<Record<AbilityFlagKey, boolean>>,
): void {
  for (const key of ABILITY_FLAG_KEYS) {
    target[key] = source[key];
  }
}

/**
 * True iff every flag in both maps has the same boolean value. Treats
 * missing keys as `false` so partial inputs (e.g. legacy rows with only
 * can_view set) compare cleanly.
 */
export function flagsEqual(
  first: Partial<Record<AbilityFlagKey, boolean>>,
  second: Partial<Record<AbilityFlagKey, boolean>>,
): boolean {
  for (const key of ABILITY_FLAG_KEYS) {
    if ((first[key] ?? false) !== (second[key] ?? false)) {
      return false;
    }
  }
  return true;
}

/**
 * Pluck just the five flag values from any object that has them — used
 * when callers want a peer-view's flags without leaking the rest of the
 * `ViewPermissionAbility` shape (which carries a `model` key too).
 */
export function flagsFrom(
  source: Readonly<Record<AbilityFlagKey, boolean>>,
): Record<AbilityFlagKey, boolean> {
  const out: Record<AbilityFlagKey, boolean> = { ...DEFAULT_ABILITY_FLAGS };
  for (const key of ABILITY_FLAG_KEYS) {
    out[key] = source[key];
  }
  return out;
}
